import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { Observable } from 'rxjs';
import { OauthService } from './oauth.service';
import {
  APPLICATION_NAMES,
  CLIENT_ID_QUERY_PARAM,
  EYEFINITY_CLIENT_ID,
  PeConnAccessToken,
} from '@share/constants';
import { NGXLogger } from 'ngx-logger';
import { HttpErrorResponse } from '@angular/common/http';
import { AuthenticationService } from '../../data-services/authentication/authentication.service';

/**
 * AuthenticationManager orchestrates the authentication flow for pe-conn.
 * It acts as a manager (VBD) that decides which authentication flow to use
 * and delegates the actual work to appropriate engines/services.
 *
 * Responsibilities:
 * - Decide when to refresh tokens
 * - Orchestrate the token refresh flow
 * - Handle authentication errors
 * - Coordinate between OauthService and AuthenticationService
 * - Determine which authentication flow to use based on client type
 */
@Injectable({
  providedIn: 'root',
})
export class AuthenticationManager {
  private oauthService = inject(OauthService);
  private authenticationService = inject(AuthenticationService);
  private logger = inject(NGXLogger);

  /**
   * Get the current access token from storage
   */
  getAccessToken(): PeConnAccessToken {
    return this.oauthService.getAccessToken();
  }

  /**
   * Subscribe to token switch events (used by support staff)
   */
  getSwitchedToken(): Observable<PeConnAccessToken> {
    return this.oauthService.getSwitchedToken();
  }

  /**
   * Orchestrates the token refresh flow.
   * This is the main entry point for authentication in the application.
   * Decides which authentication flow to use based on client type.
   *
   * @param route - The activated route snapshot containing query parameters
   * @returns Observable that emits true if refresh succeeds, false otherwise
   */
  refreshAuthToken(
    route?: ActivatedRouteSnapshot,
  ): Observable<HttpErrorResponse | boolean> {
    this.logger.debug('AuthenticationManager: Starting token refresh flow');

    // VBD Decision Logic: Determine which auth flow to use
    const isEyefinityUser = this.isEyefinityClient(route);
    const hasEfanInUrl = this.hasEfanQueryParameter(route);

    if (isEyefinityUser && hasEfanInUrl) {
      this.logger.debug(
        'AuthenticationManager: Using Eyefinity EFAN flow',
        route?.queryParams,
      );
      return this.handleEyefinityEfanFlow(route);
    } else {
      // Standard flow for EPM/Employee users
      this.logger.debug(
        'AuthenticationManager: Using standard EPM/Employee flow',
      );
      return this.oauthService.refreshAuthToken$(route);
    }
  }

  /**
   * Determines if the current user is an Eyefinity client based on the clientId.
   * Checks both the route query parameters and session storage.
   *
   * @param route - The activated route snapshot
   * @returns true if clientId matches Eyefinity client ID
   */
  private isEyefinityClient(route?: ActivatedRouteSnapshot): boolean {
    // First check if clientId is in the route query parameters
    if (route?.queryParams?.[CLIENT_ID_QUERY_PARAM]) {
      return route.queryParams[CLIENT_ID_QUERY_PARAM] === EYEFINITY_CLIENT_ID;
    }

    // If not in URL, check session storage (for subsequent page loads)
    const storedClientId = this.authenticationService.getFromSessionStorage(
      CLIENT_ID_QUERY_PARAM,
    ) as string;

    return storedClientId === EYEFINITY_CLIENT_ID;
  }

  /**
   * Checks if the route contains an EFAN query parameter.
   *
   * @param route - The activated route snapshot
   * @returns true if EFAN is present in query parameters
   */
  private hasEfanQueryParameter(route?: ActivatedRouteSnapshot): boolean {
    return !!route?.queryParams?.efan;
  }

  /**
   * Handles the Eyefinity EFAN authentication flow.
   * Extracts EFAN from URL and passes it to the OAuth service.
   *
   * @param route - The activated route snapshot containing the EFAN
   * @returns Observable that emits true if refresh succeeds, false otherwise
   */
  private handleEyefinityEfanFlow(
    route?: ActivatedRouteSnapshot,
  ): Observable<HttpErrorResponse | boolean> {
    const efan: string = route?.queryParams?.efan;

    this.logger.debug('AuthenticationManager: Extracted EFAN from URL:', efan);

    // Delegate to OauthService with EFAN flow enabled
    return this.oauthService.refreshAuthTokenWithEfan$(route, efan);
  }

  /**
   * Removes all authentication tokens from storage.
   * For encompass user, additional work is performed.
   */
  logout(): void {
    this.logger.debug('AuthenticationManager: Logging out user');
    const isAppEncompass =
      this.oauthService.getAccessToken().application ===
      APPLICATION_NAMES.encompass;
    this.oauthService.removeAllFromStorage();
    this.oauthService.stopAsyncTokenTimer();
    if (isAppEncompass) {
      this.oauthService.cleanUpForEncompass();
    }
  }

  /**
   * Initiates the login flow by redirecting to the authentication provider
   * @param overrideLocation - Optional URL to override the default login location
   */
  login(overrideLocation?: string): void {
    this.logger.debug('AuthenticationManager: Initiating login flow');
    this.oauthService.login(overrideLocation);
  }

  /**
   * Handle a visibility-resume event from the UI layer.
   *
   * This method is called when the application becomes visible again (user
   * switches back to the tab). It delegates to the OAuth service which will
   * decide whether to reschedule the token refresh timer or redirect the user
   * to the login/redirect URL if the session expired while the tab was hidden.
   */
  handleVisibilityResume(): void {
    this.oauthService.handleVisibilityResume();
  }
}
